import DepartmentSelect from '@/components/Form/Shared/DepartmentSelect'
import DepartmentSelectMutiple from '@/components/Form/Shared/DepartmentSelectMutiple'
import EmployeeSelect from '@/components/Form/Shared/EmployeeSelect'
import SubHeaders from '@/components/Headers/SubHeaders'
import Paper from '@/components/Paper'
import { useFormPersistence } from '@/hooks/useFormPersistence'
import useQueryClientCallback from '@/hooks/useQueryClientCallback'
import useUploadFile from '@/hooks/useUploadFile'
import { Attachment } from '@/modules/Upload/types'
import { generatePathDatacentral, paths } from '@/utils/path'
import { yupResolver } from '@hookform/resolvers/yup'
import { useMutationWithHandler } from '@/hooks/useMutationWithHandler'
import dayjs from 'dayjs'
import { useMemo, useState } from 'react'
import { useForm } from 'react-hook-form'
import toast from 'react-hot-toast'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { Checkbox, GridHookForm, Typogaphy } from 'vnetwork-ui'
import { createDocument } from '../../services'
import { DocumentCreatePayload } from '../../types/payload'
import { DocumentLevel, TypeDocument } from '../../types/utils'
import { documentSchema } from './yup'
import { renderFileItem } from '@/utils/function'
import TextEditorCustomize from '@/components/TextEditorCustomize'
import { QUERY_KEYS } from '@/constants/reactQueryKeys'

interface DocumentCreateProps {}

const CreateForm: React.FC<DocumentCreateProps> = () => {
    const navigate = useNavigate()
    const { t } = useTranslation()

    const [hasExpired, setHasExpired] = useState(false)

    const defaultValues: DocumentCreatePayload = useMemo(
        () => ({
            name_file: '',
            descriptions: '',
            document_level: '',
            document_type: '',
            department_id: 0,
            sharing_enable: true,
            start_day: dayjs().toDate(),
            end_day: dayjs().toDate(),
            employee_id: 0,
            departments: [],
            employees: [],
            file: [],
            file_path: '',
            assignees: [],
            document_status: [],
        }),
        []
    )

    const formControl = useForm<
        DocumentCreatePayload,
        any,
        DocumentCreatePayload
    >({
        defaultValues,
        //@ts-ignore
        resolver: yupResolver(documentSchema),
    })
    useFormPersistence(formControl, 'document-create', defaultValues)

    const { uploadAsync } = useUploadFile({
        prefix: 'documents',
        onSuccess: (attachment) => {
            if (attachment?.[0]?.status !== 'fulfilled') {
                return toast.error(t('upload-file-failed'))
            }
        },
    })
    const { refetch } = useQueryClientCallback({
        pattern: 'documents',
        mode: 'contains',
    })
    const { mutateAsync: callCreateDocument, isPending: isCreating } =
        useMutationWithHandler(
            {
                mutationFn: createDocument,
            },
            {
                reactQueryKeys: QUERY_KEYS.DOCUMENTS,
                successMessage: 'Document created successfully',
                errorMessage: 'something wrong',
                onSuccess: () => {
                    refetch()
                    navigate(generatePathDatacentral(paths.documentList, {}), {
                        replace: true,
                    })
                },
            }
        )

    const handleCreateDocument: any = async (values: DocumentCreatePayload) => {
        let attachments: Attachment[] = []
        let documentFilePath = ''

        if (values?.file) {
            const attachmentUploadeds = await uploadAsync(values?.file)

            attachments = attachmentUploadeds
                ?.filter((attachment) => attachment.status === 'fulfilled')
                .map((attachment) => attachment.value) as Attachment[]
            documentFilePath = attachments[0].attachment_file_url || ''
        }

        if (!documentFilePath) {
            toast.error(t('document_file_path_required'))
            return
        }

        await callCreateDocument({
            ...values,
            file_path: documentFilePath,
            attachments: attachments,
            end_day: hasExpired ? undefined : values.end_day,
        })
    }

    return (
        <>
            <SubHeaders
                actions={[]}
                breadcrumbs={[
                    {
                        path: paths.documentList,
                        title: t('document_list'),
                    },
                    {
                        path: paths.documentCreate,
                        title: t('create_document'),
                    },
                ]}
            />
            <Paper className="mt-2">
                <Typogaphy className="font-medium text-primary mb-4">
                    {t('create_document')}
                </Typogaphy>
                <GridHookForm
                    formControl={formControl}
                    data={[
                        {
                            formItemProps: {
                                name: 'name_file',
                                label: t('title'),
                                type: 'textfield',
                                size: 'small',
                                placeholder: t('title'),
                            },
                            className: 'col-span-4',
                        },
                        {
                            renderEditComponent: () => (
                                <TextEditorCustomize
                                    label={t('description')}
                                    placeholder={t('description')}
                                    value={{
                                        text: formControl.watch('descriptions'),
                                        attachments: [],
                                    }}
                                    onChange={(value) => {
                                        formControl.setValue(
                                            'descriptions',
                                            value.text
                                        )
                                    }}
                                    readOnly={false}
                                />
                            ),
                            className: 'col-span-4',
                        },
                        {
                            formItemProps: {
                                name: 'document_type',
                                label: t('document_type'),
                                type: 'select',
                                size: 'small',
                                placeholder: t('document_type'),
                                options: TypeDocument(t),
                            },
                            className: 'col-span-2',
                        },

                        {
                            formItemProps: {
                                name: 'document_level',
                                label: t('document_level'),
                                type: 'select',
                                size: 'small',
                                placeholder: t('document_level'),
                                options: DocumentLevel(t),
                            },
                            className: 'col-span-2',
                        },
                        {
                            className: 'col-span-2',
                            formItemProps: {
                                name: 'sharing_enable',
                                type: 'checkbox',
                                size: 'small',
                                placeholder: t('document.sharing.enable'),
                            },
                        },
                        {
                            className: 'col-span-2',
                            renderEditComponent: (control) => (
                                <DepartmentSelect
                                    label="document.department.identity"
                                    control={control}
                                    name="department_id"
                                />
                            ),
                        },
                        {
                            className: formControl.watch('sharing_enable')
                                ? 'col-span-4'
                                : 'hidden',
                            renderEditComponent: (control) => (
                                <DepartmentSelectMutiple
                                    control={control}
                                    multiple
                                    name="departments"
                                />
                            ),
                        },
                        {
                            className: formControl.watch('sharing_enable')
                                ? 'col-span-4'
                                : 'hidden',
                            renderEditComponent: (control) => (
                                <EmployeeSelect
                                    showLabel
                                    label="select_people_to_share"
                                    name="employees"
                                    control={control}
                                    multiple
                                />
                            ),
                        },
                        {
                            formItemProps: {
                                name: 'start_day',
                                label: t('start_day'),
                                type: 'datepicker',
                                size: 'small',
                                placeholder: t('start_day'),
                            },
                            className: 'col-span-2',
                        },
                        {
                            className: 'col-span-1',
                            formItemProps: {
                                disabled: !hasExpired,
                                name: 'end_day',
                                label: t('end_day'),
                                type: 'datepicker',
                                size: 'small',
                                placeholder: t('end_day'),
                            },
                        },
                        {
                            className: 'col-span-1',
                            renderEditComponent: () => (
                                <Checkbox
                                    label={t('document.expired')}
                                    checked={hasExpired}
                                    onChange={() => {
                                        setHasExpired(!hasExpired)
                                    }}
                                />
                            ),
                        },
                        {
                            renderEditComponent: (control) => (
                                <EmployeeSelect
                                    showLabel
                                    name="assignees"
                                    control={control}
                                    multiple
                                />
                            ),
                            className: 'col-span-4',
                        },
                        {
                            formItemProps: {
                                name: 'file',
                                label: t('document'),
                                type: 'files',
                                size: 'small',
                                placeholder: t('document'),
                                renderFileItem: (file: File) => (
                                    <div className="grid grid-cols-4 gap-2">
                                        <div className="col-span-1">
                                            {renderFileItem(file)}
                                        </div>
                                    </div>
                                ),
                            },
                            className: 'col-span-4',
                        },
                    ]}
                    actions={[
                        {
                            title: t('cancle-operation'),
                            type: 'button',
                            variant: 'outlined',
                            handler: () =>
                                navigate(
                                    generatePathDatacentral(
                                        paths.documentList,
                                        {}
                                    )
                                ),
                        },
                        {
                            title: t('create'),
                            type: 'submit',
                            variant: 'contained',
                            loading: isCreating,
                            disabled: isCreating,
                        },
                    ]}
                    onSubmit={handleCreateDocument}
                />
            </Paper>
        </>
    )
}

export const DocumentCreate = (props: DocumentCreateProps) => {
    return <CreateForm {...props} />
}
